<template>
	<div>
		<div class="mb-4">
			<h4 class="mb-2">User</h4>
			<CSelect
				:id="selectedUserId"
				v-model="selectedUserId"
				:options="usersOptions"
				:show-blank-option="false"
			/>
		</div>
		<CPermissionSelector
			v-if="!!selectedUser"
			:is-nested-form="isNestedForm"
			:global-permission="selectedUser.GlobalPermission.DisplayName"
			:override-permission="selectedUser.PermissionOverride?.DisplayName"
			:folder-permission-override="selectedUser.InheritedPermission?.DisplayName"
			:roles="roles"
			:assigned-workflow-roles="selectedUser.AssignedWorkflowRoles"
			is-override-menu
			@permissions-changed="handleChanges"
		/>
		<div class="flex flex--end gap-3">
			<CButton class="button--primary" :disabled="!isChanged" @click="handleUpdateUser">{{ buttonTitle }}</CButton>
		</div>
	</div>
</template>

<script lang="ts">
	import CButton from 'src/components/interaction/Button.vue';
	import CSelect from 'src/components/form-controls/Select.vue';
	import CPermissionSelector from './PermissionSelector.vue';
	import { PermissionLevels } from 'src/views/admin/utils/permission-levels';
	import type { UserWithAvatar } from './PermissionOverrideDialog.vue';
	import { updateFolderRoles, updateFormRoles } from '@cognitoforms/api/services/form-permission-service';
	import { computed, defineComponent, ref, watch, type PropType } from 'vue';
	import type { PermissionChangedEvent } from './PermissionSelector.vue';
	import type { WorkflowRoleModel } from '@cognitoforms/types/server-types/model/workflow-role-model';

	export default defineComponent({
		name: 'CAddUserPermissionDialog',
		components: {
			CButton,
			CSelect,
			CPermissionSelector
		},
		props: {
			show: {
				type: Boolean,
				default: false
			},
			users: {
				type: Array as PropType<UserWithAvatar[]>,
				default: () => []
			},
			formOrFolderId: {
				type: String,
				default: ''
			},
			isNestedForm: {
				type: Boolean,
				default: false
			},
			type: {
				type: String as PropType<'form' | 'folder'>,
				default: () => 'form'
			},
			roles: {
				type: Array as PropType<WorkflowRoleModel[]>,
				default: () => []
			},
			getName: {
				type: Function,
				required: true
			},
			currentUserEmail: {
				type: String,
				required: true
			}
		},
		emits: ['permissions-changed'],
		setup(props, { emit }) {
			const selectedUserId = ref<string>();
			const selectedPermission = ref<string>('');
			const checkedRoles = ref<number[]>(null);
			const initialPermission = ref<string>('');
			const initialRoles = ref<number[]>(null);
			const hasOverride = ref<boolean>();
			const isChanged = ref(false);

			const usersOptions = computed(() => {
				const filteredUsers = props.users.filter(u => u.Email !== props.currentUserEmail);
				return filteredUsers.map(user => ({ value: user.FormProfileId, label: props.getName(user), icon: user.avatar }));
			});

			const filteredPermissionLevels = computed(() => {
				return PermissionLevels.filter(level => level.code !== 'OW');
			});

			const selectedUser = computed<UserWithAvatar>(() => {
				return props.users.find(user => user.FormProfileId === selectedUserId.value);
			});

			const getRoleCode = (permission: string) => {
				return permission === '' ? null : PermissionLevels.find(p => p.displayValue === permission)?.code;
			};

			const handleChanges = (event: PermissionChangedEvent) => {
				checkedRoles.value = (event.workflowRoles ?? []).map(r => r.RoleId);
				selectedPermission.value = event.permission;
				isChanged.value = event.hasChanges;
			};

			const buttonTitle = computed(() => {
				if (!selectedUserId.value || selectedUserId.value === '')
					return 'Add';

				const isLimitedAccess = selectedUser.value.GlobalPermission?.Code === 'NO' || selectedUser.value.PermissionOverride?.Code === 'NO';
				if (isLimitedAccess && initialRoles.value.length === 0)
					return 'Add';

				return 'Update';
			});

			const updateUser = (userId: string) => {
				if (userId) {
					const permission = selectedUser.value.PermissionOverride?.DisplayName;
					hasOverride.value = !!permission;
					selectedPermission.value = initialPermission.value = permission;

					const roles = (selectedUser.value.AssignedWorkflowRoles ?? []).map(role => role.RoleId);;
					checkedRoles.value = initialRoles.value = roles;
				}
				else {
					selectedUserId.value = null;
					selectedPermission.value = null;
					initialPermission.value = null;
					initialRoles.value = null;
					hasOverride.value = null;
				}
			};

			watch(selectedUserId, (userId) => updateUser(userId), { immediate: true });
			watch(() => props.show, () => updateUser(null), { immediate: true });

			const handleFolderRoleUpdate = (folderId: string, role: string) => {
				const roleCode = getRoleCode(role);
				updateFolderRoles(selectedUserId.value, folderId, roleCode).then(() => {
					emit('permissions-changed');
				});
			};

			const handleFormRoleUpdate = (formId: string, role: string, workflowRoles: number[]) => {
				const roleCode = getRoleCode(role);
				updateFormRoles(selectedUserId.value, formId, roleCode, workflowRoles).then(() => {
					emit('permissions-changed');
				});
			};

			const handleUpdateUser = () => {
				if (props.type === 'form')
					handleFormRoleUpdate(props.formOrFolderId, selectedPermission.value, checkedRoles.value);
				else
					handleFolderRoleUpdate(props.formOrFolderId, selectedPermission.value);
			};

			return {
				selectedUser,
				filteredPermissionLevels,
				usersOptions,
				selectedUserId,
				isChanged,
				buttonTitle,
				handleChanges,
				handleUpdateUser
			};
		}
	});
</script>